import axios from 'axios';
import { API, BACKEND_URL } from '@/config';

// Helper to get auth headers
export const getAuthHeaders = (token) => {
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Products API
export const productsApi = {
  getAll: (activeOnly = true, headers = {}) => 
    axios.get(`${API}/products?active_only=${activeOnly}`, { headers }),
  getById: (id, headers = {}) => 
    axios.get(`${API}/products/${id}`, { headers }),
  create: (data, headers) => 
    axios.post(`${API}/products`, data, { headers }),
  update: (id, data, headers) => 
    axios.put(`${API}/products/${id}`, data, { headers }),
  delete: (id, headers) => 
    axios.delete(`${API}/products/${id}`, { headers }),
};

// Recipes API
export const recipesApi = {
  getAll: (activeOnly = true, headers = {}) => 
    axios.get(`${API}/recipes?active_only=${activeOnly}`, { headers }),
  getById: (id, headers = {}) => 
    axios.get(`${API}/recipes/${id}`, { headers }),
  create: (data, headers) => 
    axios.post(`${API}/recipes`, data, { headers }),
  update: (id, data, headers) => 
    axios.put(`${API}/recipes/${id}`, data, { headers }),
  delete: (id, headers) => 
    axios.delete(`${API}/recipes/${id}`, { headers }),
};

// Cart API
export const cartApi = {
  get: (sessionId, headers = {}) => 
    axios.get(`${API}/cart`, { headers: { ...headers, 'X-Session-Id': sessionId } }),
  add: (data, headers = {}) => 
    axios.post(`${API}/cart/add`, data, { headers: { ...headers, 'X-Session-Id': data.session_id } }),
  update: (data, headers = {}) => 
    axios.put(`${API}/cart/update`, data, { headers: { ...headers, 'X-Session-Id': data.session_id } }),
  remove: (productId, sessionId, headers = {}) => 
    axios.delete(`${API}/cart/remove/${productId}`, { headers: { ...headers, 'X-Session-Id': sessionId } }),
  clear: (sessionId, headers = {}) => 
    axios.delete(`${API}/cart/clear`, { headers: { ...headers, 'X-Session-Id': sessionId } }),
};

// Orders API
export const ordersApi = {
  getAll: (sessionId, headers = {}) => 
    axios.get(`${API}/orders?session_id=${sessionId}`, { headers }),
  create: (data, headers = {}) => 
    axios.post(`${API}/orders`, data, { headers }),
  createPaymentIntent: (data, headers = {}) => 
    axios.post(`${API}/create-payment-intent`, data, { headers }),
  confirmPayment: (data, headers = {}) => 
    axios.post(`${API}/confirm-payment`, data, { headers }),
  updateStatus: (id, data, headers) => 
    axios.put(`${API}/orders/${id}/status`, data, { headers }),
};

// Auth API
export const authApi = {
  login: (data) => 
    axios.post(`${API}/auth/login`, data),
  register: (data) => 
    axios.post(`${API}/auth/register`, data),
  getProfile: (headers) => 
    axios.get(`${API}/auth/me`, { headers }),
};

// Posts API
export const postsApi = {
  getAll: (headers = {}) => 
    axios.get(`${API}/posts`, { headers }),
  getById: (id, headers = {}) => 
    axios.get(`${API}/posts/${id}`, { headers }),
  create: (data, headers) => 
    axios.post(`${API}/posts`, data, { headers }),
  like: (id, headers) => 
    axios.post(`${API}/posts/${id}/like`, {}, { headers }),
  save: (id, headers) => 
    axios.post(`${API}/posts/${id}/save`, {}, { headers }),
  share: (id) => 
    axios.post(`${API}/posts/${id}/share`),
  getComments: (id) => 
    axios.get(`${API}/posts/${id}/comments`),
  addComment: (id, data, headers) => 
    axios.post(`${API}/posts/${id}/comments`, data, { headers }),
};

// Users API
export const usersApi = {
  getProfile: (id, headers = {}) => 
    axios.get(`${API}/users/${id}/profile`, { headers }),
  updateProfile: (id, data, headers) => 
    axios.put(`${API}/users/${id}/profile`, data, { headers }),
  getPosts: (id, headers = {}) => 
    axios.get(`${API}/users/${id}/posts`, { headers }),
  follow: (id, headers) => 
    axios.post(`${API}/users/${id}/follow`, {}, { headers }),
  search: (query, headers = {}) => 
    axios.get(`${API}/search/users?q=${encodeURIComponent(query)}`, { headers }),
};

// Messages API
export const messagesApi = {
  getConversations: (headers) => 
    axios.get(`${API}/messages/conversations`, { headers }),
  getMessages: (conversationId, headers) => 
    axios.get(`${API}/messages/${conversationId}`, { headers }),
  send: (data, headers) => 
    axios.post(`${API}/messages`, data, { headers }),
  createConversation: (data, headers) => 
    axios.post(`${API}/messages/conversations`, data, { headers }),
};

// Community API
export const communityApi = {
  getStats: () => 
    axios.get(`${API}/community/stats`),
  searchPosts: (query, headers = {}) => 
    axios.get(`${API}/search/posts?q=${encodeURIComponent(query)}`, { headers }),
};

// Admin API
export const adminApi = {
  getStats: (headers) => 
    axios.get(`${API}/admin/stats`, { headers }),
  getAbout: (headers) => 
    axios.get(`${API}/settings/about`, { headers }),
  updateAbout: (data, headers) => 
    axios.put(`${API}/settings/about`, data, { headers }),
};

// Upload API
export const uploadApi = {
  image: (formData, headers) => 
    axios.post(`${API}/upload/image`, formData, { 
      headers: { ...headers, 'Content-Type': 'multipart/form-data' } 
    }),
  video: (formData, headers) => 
    axios.post(`${API}/upload/video`, formData, { 
      headers: { ...headers, 'Content-Type': 'multipart/form-data' } 
    }),
  multiple: (formData, headers) => 
    axios.post(`${API}/upload/multiple`, formData, { 
      headers: { ...headers, 'Content-Type': 'multipart/form-data' } 
    }),
};
